import React, { useEffect, useRef } from 'react';
import { useScroll, useTransform, motion } from 'framer-motion';
import { useReducedMotion } from '../hooks/useReducedMotion';
export function AnimatedBackground() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const prefersReducedMotion = useReducedMotion();
  const {
    scrollY
  } = useScroll();
  const amplitude = useTransform(scrollY, [0, 500], [1, 0.75]);
  useEffect(() => {
    if (prefersReducedMotion) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    let animationFrameId: number;
    let time = 0;
    let currentAmplitude = 1;
    const unsubscribe = amplitude.on('change', v => {
      currentAmplitude = v;
    });
    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();
    window.addEventListener('resize', resize);
    const particles: Array<{
      x: number;
      y: number;
      vx: number;
      vy: number;
      size: number;
      opacity: number;
    }> = [];
    const particleCount = 80;
    for (let i = 0; i < particleCount; i++) {
      particles.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        vx: (Math.random() - 0.5) * 0.8,
        vy: (Math.random() - 0.5) * 0.8,
        size: Math.random() * 2 + 1,
        opacity: Math.random() * 0.5 + 0.2
      });
    }
    const formulas = ['σ²', 'E[X]', 'Δ', 'θ', 'ρ', 'β', 'α', 'λ', '∫', '∂'];
    const floatingTexts: Array<{
      text: string;
      x: number;
      y: number;
      vy: number;
      opacity: number;
      size: number;
    }> = [];
    for (let i = 0; i < 12; i++) {
      floatingTexts.push({
        text: formulas[Math.floor(Math.random() * formulas.length)],
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        vy: -0.3 - Math.random() * 0.3,
        opacity: 0.1 + Math.random() * 0.15,
        size: 20 + Math.random() * 25
      });
    }
    const gridLines: Array<{
      x1: number;
      y1: number;
      x2: number;
      y2: number;
      opacity: number;
      speed: number;
    }> = [];
    for (let i = 0; i < 8; i++) {
      const isHorizontal = Math.random() > 0.5;
      if (isHorizontal) {
        gridLines.push({
          x1: 0,
          y1: Math.random() * canvas.height,
          x2: canvas.width,
          y2: Math.random() * canvas.height,
          opacity: 0.03 + Math.random() * 0.05,
          speed: 0.2 + Math.random() * 0.3
        });
      } else {
        gridLines.push({
          x1: Math.random() * canvas.width,
          y1: 0,
          x2: Math.random() * canvas.width,
          y2: canvas.height,
          opacity: 0.03 + Math.random() * 0.05,
          speed: 0.2 + Math.random() * 0.3
        });
      }
    }
    const animate = () => {
      time += 0.01;
      ctx.fillStyle = 'rgba(0, 31, 63, 0.05)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      gridLines.forEach(line => {
        const pulse = Math.sin(time * line.speed) * 0.5 + 0.5;
        ctx.beginPath();
        ctx.moveTo(line.x1, line.y1);
        ctx.lineTo(line.x2, line.y2);
        ctx.strokeStyle = `rgba(30, 58, 138, ${line.opacity * pulse * currentAmplitude})`;
        ctx.lineWidth = 1;
        ctx.stroke();
      });
      particles.forEach((particle, i) => {
        particle.x += particle.vx;
        particle.y += particle.vy;
        if (particle.x < 0) particle.x = canvas.width;
        if (particle.x > canvas.width) particle.x = 0;
        if (particle.y < 0) particle.y = canvas.height;
        if (particle.y > canvas.height) particle.y = 0;
        const gradient = ctx.createRadialGradient(particle.x, particle.y, 0, particle.x, particle.y, particle.size * 3);
        gradient.addColorStop(0, `rgba(30, 58, 138, ${particle.opacity * currentAmplitude})`);
        gradient.addColorStop(1, 'rgba(30, 58, 138, 0)');
        ctx.beginPath();
        ctx.arc(particle.x, particle.y, particle.size * 3, 0, Math.PI * 2);
        ctx.fillStyle = gradient;
        ctx.fill();
        ctx.beginPath();
        ctx.arc(particle.x, particle.y, particle.size, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(248, 250, 252, ${particle.opacity * 0.8 * currentAmplitude})`;
        ctx.fill();
        particles.forEach((otherParticle, j) => {
          if (i === j) return;
          const dx = particle.x - otherParticle.x;
          const dy = particle.y - otherParticle.y;
          const distance = Math.sqrt(dx * dx + dy * dy);
          if (distance < 150) {
            ctx.beginPath();
            ctx.moveTo(particle.x, particle.y);
            ctx.lineTo(otherParticle.x, otherParticle.y);
            const opacity = (1 - distance / 150) * 0.15 * currentAmplitude;
            ctx.strokeStyle = `rgba(30, 58, 138, ${opacity})`;
            ctx.lineWidth = 1;
            ctx.stroke();
          }
        });
      });
      floatingTexts.forEach(text => {
        text.y += text.vy;
        if (text.y < -50) {
          text.y = canvas.height + 50;
          text.x = Math.random() * canvas.width;
        }
        ctx.font = `${text.size}px "Cinzel", serif`;
        ctx.fillStyle = `rgba(248, 250, 252, ${text.opacity * (0.5 + Math.sin(time * 2 + text.x) * 0.5) * currentAmplitude})`;
        ctx.fillText(text.text, text.x, text.y);
      });
      ctx.beginPath();
      for (let x = 0; x < canvas.width; x += 5) {
        const y = canvas.height / 2 + Math.sin((x + time * 50) * 0.01) * 30 * currentAmplitude + Math.sin((x + time * 30) * 0.02) * 20 * currentAmplitude;
        if (x === 0) {
          ctx.moveTo(x, y);
        } else {
          ctx.lineTo(x, y);
        }
      }
      ctx.strokeStyle = `rgba(30, 58, 138, ${0.08 * currentAmplitude})`;
      ctx.lineWidth = 2;
      ctx.stroke();
      ctx.beginPath();
      for (let x = 0; x < canvas.width; x += 5) {
        const y = canvas.height / 2 + Math.sin((x - time * 40) * 0.015) * 40 * currentAmplitude + Math.sin((x - time * 25) * 0.025) * 25 * currentAmplitude;
        if (x === 0) {
          ctx.moveTo(x, y);
        } else {
          ctx.lineTo(x, y);
        }
      }
      ctx.strokeStyle = `rgba(248, 250, 252, ${0.06 * currentAmplitude})`;
      ctx.lineWidth = 2;
      ctx.stroke();
      animationFrameId = requestAnimationFrame(animate);
    };
    animate();
    return () => {
      window.removeEventListener('resize', resize);
      cancelAnimationFrame(animationFrameId);
      unsubscribe();
    };
  }, [prefersReducedMotion, amplitude]);
  if (prefersReducedMotion) {
    return <div className="absolute inset-0 w-full h-full opacity-20" data-id="element-18">
        <svg width="100%" height="100%" data-id="element-19">
          <defs data-id="element-20">
            <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse" data-id="element-21">
              <path d="M 40 0 L 0 0 0 40" fill="none" stroke="rgba(30, 58, 138, 0.1)" strokeWidth="1" data-id="element-22" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#grid)" data-id="element-23" />
        </svg>
      </div>;
  }
  return <canvas ref={canvasRef} className="absolute inset-0 w-full h-full" style={{
    mixBlendMode: 'screen'
  }} data-id="element-24" />;
}